import React, { useMemo } from 'react';
import { TopVenuesPanel } from '../../components/dashboard/TopVenuesPanel';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import { DashboardInsightChip } from '../../components/dashboard/DashboardInsightChip';
import { ConcentrationStrip } from '../../components/dashboard/ConcentrationStrip';
import ErrorBoundary from '../../components/ErrorBoundary';
import { SkeletonChart } from '../../components/ui/SkeletonBlock';
import { buildVenueSummaryStats } from '../../lib/pageSummaryStats';
import { buildConcentrationRows, buildVenuesInsight } from '../../lib/dashboardInsights';

export function VenuesPage({
  committedFilters,
  data,
  currencyCode,
  panelLoading,
  error,
  isFirstLoad,
  currencyUnavailable = false,
  filterSlot = null,
}) {
  const queryKey = committedFilters.queryString || '';
  const venueStats = useMemo(
    () =>
      buildVenueSummaryStats(data?.topVenues || [], {
        currencyCode,
        currencyUnavailable,
      }),
    [data?.topVenues, currencyCode, currencyUnavailable]
  );
  const insight = useMemo(
    () => buildVenuesInsight(data?.topVenues || [], currencyCode),
    [data?.topVenues, currencyCode]
  );
  const concentration = useMemo(
    () => buildConcentrationRows(data?.topVenues || [], 'venueName'),
    [data?.topVenues]
  );

  return (
    <section className="flex w-full min-w-0 flex-col gap-5">
      <PageSummaryStats
        stats={venueStats}
        isFirstLoad={isFirstLoad}
        error={error}
        emptyMessage="No venue data found for this period. Try a different date range or filter."
      />

      {filterSlot}

      {!isFirstLoad && !error && insight ? (
        <DashboardInsightChip tone={insight.tone} title={insight.title} detail={insight.detail} />
      ) : null}

      {isFirstLoad ? (
        <div className="grid gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2"><SkeletonChart height="h-[320px]" /></div>
          <SkeletonChart height="h-[320px]" />
        </div>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-3">
          <div className="min-w-0 xl:col-span-2">
            <ErrorBoundary key={`venues-${queryKey}`}>
              <TopVenuesPanel
                topVenues={data?.topVenues || []}
                currencyCode={currencyCode}
                loading={panelLoading}
                error={error}
                variant="detailed"
              />
            </ErrorBoundary>
          </div>
          <ConcentrationStrip title="Venue concentration" rows={concentration} />
        </div>
      )}
    </section>
  );
}
